import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import JSZip from 'jszip'
import { useThemeMode } from '@/contexts/ThemeContext'
import { useUser } from '@/hooks/useUser'
import useIsMobile from '@/hooks/useIsMobile'
import { fetchWithAuth } from '@/lib/api/client'
import { normalizeEmojis } from '@/lib/emojiUtils'
import Button from './ui/Button'
import Message from './ui/Message'

interface ImportChatGPTProps {
  onClose: () => void
  onOpenSubscription?: () => void
}

interface ParsedMessage {
  role: 'user' | 'assistant'
  content: string
  created_at?: number // ChatGPT timestamp (Unix timestamp in seconds)
}

interface PreviewInfo {
  messageCount: number
  isSubscriber: boolean
  hasAlreadyImported?: boolean
  originalMessageCount?: number
  wasTrimmed?: boolean
}

interface SubscriptionStatus {
  plan: 'free' | 'paid'
  status: string | null
  currentPeriodEnd: number | null
  cancelAtPeriodEnd: boolean
}

/**
 * Modal component for displaying instructions on how to export data from ChatGPT
 * and uploading/importing the exported ZIP file.
 */
const MAX_MESSAGES = 50000

export default function ImportChatGPT({ onClose, onOpenSubscription }: ImportChatGPTProps) {
  const { colors } = useThemeMode()
  const { user } = useUser()
  const isMobile = useIsMobile()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<{ messageCount: number } | null>(null)
  const [progress, setProgress] = useState<string>('')
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null)
  const [isFetchingSubscription, setIsFetchingSubscription] = useState(true)
  const [previewInfo, setPreviewInfo] = useState<PreviewInfo | null>(null)
  const [trimNotice, setTrimNotice] = useState<string | null>(null)

  // Get font mode from localStorage to match the app's font setting
  const fontMode = typeof window !== 'undefined'
    ? (localStorage.getItem('kyogre-font-mode') as 'departure-mono' | 'open-dyslexic' | 'system' || 'departure-mono')
    : 'departure-mono'

  // Fetch subscription status on mount
  useEffect(() => {
    const fetchSubscription = async () => {
      setIsFetchingSubscription(true)
      try {
        const url = `${import.meta.env.VITE_API_URL}/api/billing/subscription?refresh=true`
        const response = await fetchWithAuth(url)

        if (response.ok) {
          const data = await response.json()
          setSubscription(data)
        } else {
          // Default to free plan if fetch fails
          setSubscription({
            plan: 'free',
            status: null,
            currentPeriodEnd: null,
            cancelAtPeriodEnd: false,
          })
        }
      } catch (error) {
        console.error('Error fetching subscription:', error)
        // Default to free plan on error
        setSubscription({
          plan: 'free',
          status: null,
          currentPeriodEnd: null,
          cancelAtPeriodEnd: false,
        })
      } finally {
        setIsFetchingSubscription(false)
      }
    }

    fetchSubscription()
  }, [])

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isProcessing && !isUploading) {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscapeKey)
    return () => {
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [onClose, isProcessing, isUploading])

  const isSubscriber = subscription?.plan === 'paid'

  /**
   * Extracts and parses conversations.json from a ChatGPT export ZIP file.
   */
  const parseChatGPTExport = async (file: File): Promise<ParsedMessage[]> => {
    setProgress('Reading ZIP file...')
    const zip = new JSZip()
    const zipData = await zip.loadAsync(file)

    // Find conversations.json in the ZIP
    const conversationsFile = zipData.file('conversations.json')
    if (!conversationsFile) {
      throw new Error('conversations.json not found in ZIP file. Please ensure you exported the correct file from ChatGPT.')
    }

    setProgress('Parsing conversations...')
    const conversationsJson = await conversationsFile.async('text')
    const conversations = JSON.parse(conversationsJson)

    // ChatGPT export structure: array of conversations, each with messages
    if (!Array.isArray(conversations)) {
      throw new Error('Invalid conversations.json format. Expected an array of conversations.')
    }

    const allMessages: ParsedMessage[] = []

    for (const conversation of conversations) {
      if (!conversation.mapping || typeof conversation.mapping !== 'object') {
        continue
      }

      // Extract messages from the mapping object
      const messageEntries = Object.values(conversation.mapping) as any[]
      
      // Sort messages by create_time if available to maintain chronological order
      const sortedMessages = messageEntries
        .filter((entry: any) => entry?.message)
        .sort((a: any, b: any) => {
          const timeA = a.message?.create_time || 0
          const timeB = b.message?.create_time || 0
          return timeA - timeB
        })

      for (const entry of sortedMessages) {
        const message = entry.message
        if (!message) continue

        // ChatGPT message structure: message.author.role can be 'user' or 'assistant'
        const author = message.author
        if (!author || !author.role) continue

        let role: 'user' | 'assistant'
        if (author.role === 'user') {
          role = 'user'
        } else if (author.role === 'assistant') {
          role = 'assistant'
        } else {
          continue // Skip system messages or other roles
        }

        // Extract content - can be text or array of content parts
        // Filter out images and only keep text content
        let content = ''
        if (message.content) {
          if (typeof message.content === 'string') {
            content = message.content
          } else if (message.content.parts && Array.isArray(message.content.parts)) {
            // Content can be an object with parts array
            // Filter out images (objects with type 'image' or image_url) and only keep text
            const textParts = message.content.parts
              .filter((part: any) => {
                // Keep string parts
                if (typeof part === 'string') return true
                // Skip image objects
                if (typeof part === 'object') {
                  // Skip if it's an image type or has image_url
                  if (part.type === 'image' || part.image_url || part.image) {
                    return false
                  }
                  // Try to extract text from object
                  if (part.text) return true
                }
                return false
              })
              .map((part: any) => {
                // Extract text from objects, keep strings as-is
                if (typeof part === 'string') return part
                if (part.text) return part.text
                return ''
              })
              .filter((text: string) => text.trim().length > 0)
            
            content = textParts.join('\n')
          } else if (typeof message.content === 'object') {
            // Try to extract text from content object, skip if it's an image
            if (message.content.type === 'image' || message.content.image_url || message.content.image) {
              continue // Skip image-only messages
            }
            content = message.content.text || ''
          }
        }

        // Only add messages with non-empty text content
        // Also limit content size to prevent oversized messages (max 50KB per message)
        const trimmedContent = content.trim()
        if (trimmedContent) {
          const maxContentSize = 50 * 1024 // 50KB
          const finalContent = trimmedContent.length > maxContentSize
            ? trimmedContent.substring(0, maxContentSize) + '\n\n[Content truncated due to size]'
            : trimmedContent
          
          // Extract timestamp from message (create_time is typically in seconds)
          const timestamp = message.create_time
          
          allMessages.push({ 
            role, 
            content: finalContent,
            ...(timestamp && { created_at: timestamp })
          })
        }
      }
    }

    if (allMessages.length === 0) {
      throw new Error('No valid messages found in conversations.json. The file may be empty or in an unexpected format.')
    }

    return allMessages
  }

  /**
   * Handles file selection from input or drag-and-drop.
   */
  const handleFileSelect = async (file: File | null) => {
    if (!file) return

    // Validate file type
    if (!file.name.endsWith('.zip') && !file.type.includes('zip')) {
      setError('Please select a ZIP file exported from ChatGPT.')
      return
    }

    setSelectedFile(file)
    setError(null)
    setSuccess(null)
    setProgress('')

    // Auto-process the file
    await handleProcessFile(file)
  }

  /**
   * Processes the selected file: extracts, parses, and uploads to backend.
   */
  const handleProcessFile = async (file: File) => {
    if (!user) {
      setError('You must be logged in to import conversations.')
      return
    }

    setIsProcessing(true)
    setError(null)
    setSuccess(null)
    setPreviewInfo(null)
    setTrimNotice(null)

    try {
      // Parse the ZIP file
      const parsedMessages = await parseChatGPTExport(file)
      const trimmedCount = Math.max(parsedMessages.length - MAX_MESSAGES, 0)
      let payloadMessages = parsedMessages

      if (trimmedCount > 0) {
        payloadMessages = parsedMessages.slice(-MAX_MESSAGES)
        setTrimNotice(
          `We found ${parsedMessages.length.toLocaleString()} messages in your export. To stay within the ${MAX_MESSAGES.toLocaleString()} message limit, we will import only the most recent ${MAX_MESSAGES.toLocaleString()} messages.`
        )
      }

      const API_URL = import.meta.env.VITE_API_URL

      // If not a subscriber, show preview only
      if (!isSubscriber) {
        setProgress(`Found ${payloadMessages.length.toLocaleString()} importable text messages. Checking subscription status...`)
        
        const previewResponse = await fetchWithAuth(`${API_URL}/api/chat/import-chatgpt`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ messages: payloadMessages, previewOnly: true }),
        })

        if (!previewResponse.ok) {
          const errorData = await previewResponse.json()
          throw new Error(errorData.error || errorData.message || 'Failed to preview conversations')
        }

        const previewData = await previewResponse.json()
        setPreviewInfo({
          messageCount: previewData.messageCount || payloadMessages.length,
          isSubscriber: previewData.isSubscriber || false,
          hasAlreadyImported: previewData.hasAlreadyImported || false,
          originalMessageCount: parsedMessages.length,
          wasTrimmed: trimmedCount > 0 || previewData.wasTrimmed,
        })
        setProgress('')
        return
      }

      // For subscribers, proceed with actual import (if not already imported)
      setProgress(`Found ${payloadMessages.length.toLocaleString()} importable text messages. Uploading in batches...`)

      // Upload to backend
      setIsUploading(true)
      const response = await fetchWithAuth(`${API_URL}/api/chat/import-chatgpt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ messages: payloadMessages }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        // Check if it's an "already imported" error
        if (errorData.hasAlreadyImported) {
          setPreviewInfo({
            messageCount: payloadMessages.length,
            isSubscriber: true,
            hasAlreadyImported: true,
            originalMessageCount: parsedMessages.length,
            wasTrimmed: trimmedCount > 0,
          })
          setProgress('')
          return
        }
        throw new Error(errorData.error || errorData.message || 'Failed to import conversations')
      }

      const data = await response.json()
      setSuccess({ messageCount: data.messageCount || payloadMessages.length })
      setProgress('')
      setSelectedFile(null)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred'
      setError(errorMessage)
      setProgress('')
    } finally {
      setIsProcessing(false)
      setIsUploading(false)
    }
  }

  /**
   * Handles drag and drop events.
   */
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isProcessing && !isUploading) {
      setIsDragging(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    if (isProcessing || isUploading) return

    const files = e.dataTransfer.files
    if (files.length > 0) {
      handleFileSelect(files[0])
    }
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      handleFileSelect(files[0])
    }
  }

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: isMobile ? colors.primaryBg : 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'center',
        zIndex: 999999,
        padding: isMobile ? 0 : '40px',
        backdropFilter: isMobile ? 'none' : 'blur(8px)',
        fontFamily:
          fontMode === 'departure-mono'
            ? 'var(--font-departure-mono), monospace'
            : fontMode === 'open-dyslexic'
              ? 'var(--font-open-dyslexic), sans-serif'
              : '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
      onClick={isMobile ? undefined : onClose}
    >
      <div
        style={{
          backgroundColor: colors.primaryBg,
          borderRadius: isMobile ? 0 : '10px',
          border: isMobile ? 'none' : `2px solid ${colors.primaryBorder}`,
          padding: isMobile ? '16px' : '24px',
          paddingTop: isMobile ? 'calc(16px + env(safe-area-inset-top))' : '24px',
          paddingBottom: isMobile ? 'calc(16px + env(safe-area-inset-bottom))' : '24px',
          maxWidth: isMobile ? '100%' : '700px',
          width: isMobile ? '100%' : '90%',
          height: isMobile ? '100%' : 'auto',
          maxHeight: isMobile ? '100%' : 'calc(100vh - 80px)',
          overflowY: 'auto',
          position: 'relative',
          boxShadow: isMobile ? 'none' : '0 20px 60px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.05)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            backgroundColor: colors.secondaryBg,
            border: `1px solid ${colors.primaryBorder}`,
            color: colors.primaryText,
            cursor: 'pointer',
            fontSize: '18px',
            padding: '6px 10px',
            lineHeight: '1',
            borderRadius: '6px',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = colors.primaryBorder
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = colors.secondaryBg
          }}
        >
          ✕
        </button>

        {/* Header */}
        <h2
          style={{
            fontSize: isMobile ? '20px' : '22px',
            fontWeight: '700',
            marginBottom: isMobile ? '16px' : '20px',
            color: colors.primaryText,
            letterSpacing: '-0.3px',
            paddingRight: '40px',
          }}
        >
          <span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('📥')}</span> Import from ChatGPT
        </h2>

        {/* Instructions Section */}
        <div
          style={{
            backgroundColor: colors.secondaryBg,
            border: `1px solid ${colors.primaryBorder}`,
            borderRadius: isMobile ? '12px' : '8px',
            padding: isMobile ? '14px' : '18px',
            marginBottom: isMobile ? '12px' : '16px',
          }}
        >
          <h3
            style={{
              fontSize: isMobile ? '14px' : '15px',
              fontWeight: '700',
              marginBottom: isMobile ? '12px' : '16px',
              color: colors.primaryText,
              letterSpacing: '-0.2px',
            }}
          >
            How to Export Your Data from ChatGPT
          </h3>
          <ol
            style={{
              listStyle: 'decimal',
              paddingLeft: isMobile ? '16px' : '20px',
              margin: 0,
            }}
          >
            <li
              style={{
                fontSize: isMobile ? '13px' : '14px',
                color: colors.primaryText,
                marginBottom: isMobile ? '8px' : '12px',
                lineHeight: '1.5',
              }}
            >
              Sign in to ChatGPT at{' '}
              <a
                href="https://chat.openai.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: colors.visualAccent1,
                  textDecoration: 'underline',
                }}
              >
                chat.openai.com
              </a>
            </li>
            <li
              style={{
                fontSize: isMobile ? '13px' : '14px',
                color: colors.primaryText,
                marginBottom: isMobile ? '8px' : '12px',
                lineHeight: '1.5',
              }}
            >
              Tap your profile icon → Settings
            </li>
            <li
              style={{
                fontSize: isMobile ? '13px' : '14px',
                color: colors.primaryText,
                marginBottom: isMobile ? '8px' : '12px',
                lineHeight: '1.5',
              }}
            >
              Data Controls → Export data
            </li>
            <li
              style={{
                fontSize: isMobile ? '13px' : '14px',
                color: colors.primaryText,
                marginBottom: 0,
                lineHeight: '1.5',
              }}
            >
              Download the ZIP when emailed
            </li>
          </ol>
        </div>

        {/* File Upload Section */}
        <div
          style={{
            backgroundColor: colors.secondaryBg,
            border: `1px solid ${colors.primaryBorder}`,
            borderRadius: isMobile ? '12px' : '8px',
            padding: isMobile ? '14px' : '18px',
            marginBottom: isMobile ? '12px' : '16px',
          }}
        >
          <h3
            style={{
              fontSize: isMobile ? '14px' : '15px',
              fontWeight: '700',
              marginBottom: '12px',
              color: colors.primaryText,
              letterSpacing: '-0.2px',
            }}
          >
            Upload Your Export
          </h3>

          {!isFetchingSubscription && !isSubscriber && (
            <div
              style={{
                backgroundColor: colors.primaryBg,
                border: `1px solid ${colors.primaryBorder}`,
                borderRadius: isMobile ? '10px' : '6px',
                padding: isMobile ? '10px' : '12px',
                marginBottom: '12px',
                fontSize: isMobile ? '12px' : '13px',
                color: colors.secondaryText,
              }}
            >
              <span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('💡')}</span> <strong>Preview Mode:</strong> Free users can preview their export. Subscription required to import.
            </div>
          )}

          <div
            style={{
              backgroundColor: colors.primaryBg,
              border: `1px solid ${colors.visualAccent1 || colors.primaryBorder}`,
              borderRadius: isMobile ? '10px' : '6px',
              padding: isMobile ? '10px' : '12px',
              marginBottom: '12px',
            }}
          >
            <div style={{ fontSize: isMobile ? '12px' : '13px', fontWeight: '700', marginBottom: '4px', color: colors.primaryText }}>
              <span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('⚠️')}</span> One-Time Feature
            </div>
            <div style={{ fontSize: isMobile ? '12px' : '13px', color: colors.secondaryText, lineHeight: '1.4' }}>
              This import can only be used <strong>once per account</strong>. Max {MAX_MESSAGES.toLocaleString()} messages.
            </div>
          </div>

          {/* Drag and Drop Area */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !isProcessing && !isUploading && fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${isDragging ? colors.visualAccent1 : colors.primaryBorder}`,
              borderRadius: isMobile ? '12px' : '8px',
              padding: isMobile ? '40px 20px' : '32px',
              textAlign: 'center',
              cursor: isProcessing || isUploading ? 'default' : 'pointer',
              backgroundColor: isDragging ? colors.secondaryBg : (isMobile ? colors.secondaryBg : 'transparent'),
              transition: 'all 0.2s ease',
              marginBottom: '12px',
              wordBreak: 'break-word',
              minHeight: isMobile ? '120px' : 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip,application/zip"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
              disabled={isProcessing || isUploading}
            />
            {selectedFile ? (
              <div>
                <div style={{ fontSize: isMobile ? '18px' : '16px', marginBottom: '8px', color: colors.primaryText }}>
                  <span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('📦')}</span> {selectedFile.name}
                </div>
                <div style={{ fontSize: isMobile ? '14px' : '12px', color: colors.secondaryText }}>
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: isMobile ? '40px' : '24px', marginBottom: '12px', fontFamily: "'Noto Emoji', sans-serif" }}>
                  {normalizeEmojis('📥')}
                </div>
                <div style={{ fontSize: isMobile ? '16px' : '16px', marginBottom: '8px', color: colors.primaryText, fontWeight: 600 }}>
                  {isMobile ? 'Tap to select your ZIP file' : 'Drop your ZIP file here'}
                </div>
                <div style={{ fontSize: isMobile ? '14px' : '12px', color: colors.secondaryText }}>
                  {isMobile ? 'Select the export from ChatGPT' : 'or click to browse'}
                </div>
              </div>
            )}
          </div>

          {/* Progress/Status Messages */}
          {progress && (
            <Message variant="loading" style={{ marginBottom: '12px' }}>
              {progress}
            </Message>
          )}

          {trimNotice && (
            <Message variant="info" centered={false} style={{ marginBottom: '12px' }}>
              {trimNotice}
            </Message>
          )}

          {error && (
            <Message variant="error" style={{ marginBottom: '12px' }}>
              {error}
            </Message>
          )}

          {previewInfo && !isSubscriber && (
            <div
              style={{
                backgroundColor: colors.secondaryBg,
                border: `2px solid ${colors.primaryBorder}`,
                borderRadius: '8px',
                padding: '18px',
                marginBottom: '12px',
              }}
            >
              <div style={{ fontSize: '15px', fontWeight: '700', marginBottom: '12px', color: colors.primaryText }}>
                <span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('📊')}</span> Preview
              </div>
              <div style={{ fontSize: '14px', color: colors.primaryText, marginBottom: '16px', lineHeight: '1.6' }}>
                Your export contains{' '}
                <strong>{(previewInfo.originalMessageCount || previewInfo.messageCount).toLocaleString()}</strong>{' '}
                messages.
                {previewInfo.wasTrimmed ? (
                  <div style={{ color: colors.secondaryText, marginTop: '8px' }}>
                    We will import the most recent {MAX_MESSAGES.toLocaleString()} messages to stay within the limit.
                  </div>
                ) : (
                  <div style={{ color: colors.secondaryText, marginTop: '8px' }}>
                    All of them are within the {MAX_MESSAGES.toLocaleString()} message limit.
                  </div>
                )}
              </div>
              <div
                style={{
                  backgroundColor: colors.primaryBg,
                  border: `1px solid ${colors.primaryBorder}`,
                  borderRadius: '6px',
                  padding: '12px',
                  marginBottom: '12px',
                }}
              >
                <div style={{ fontSize: '13px', color: colors.secondaryText, marginBottom: '8px' }}>
                  <span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('💳')}</span> Subscription Required
                </div>
                <div style={{ fontSize: '14px', color: colors.primaryText, lineHeight: '1.6' }}>
                  To import your ChatGPT conversations, you need an active subscription. Upgrade now to unlock this feature and help Honcho build a better representation of you.
                </div>
              </div>
              {onOpenSubscription && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    onOpenSubscription()
                    onClose()
                  }}
                  style={{ width: '100%' }}
                >
                  Upgrade to Import
                </Button>
              )}
            </div>
          )}

          {previewInfo && isSubscriber && previewInfo.hasAlreadyImported && (
            <div
              style={{
                backgroundColor: colors.secondaryBg,
                border: `2px solid ${colors.primaryBorder}`,
                borderRadius: '8px',
                padding: '18px',
                marginBottom: '12px',
              }}
            >
              <div style={{ fontSize: '15px', fontWeight: '700', marginBottom: '12px', color: colors.primaryText }}>
                <span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('⚠️')}</span> Already Imported
              </div>
              <div style={{ fontSize: '14px', color: colors.primaryText, lineHeight: '1.6' }}>
                You have already imported your ChatGPT conversations. This feature can only be used once per account.
              </div>
            </div>
          )}

          {success && (
            <Message variant="success" style={{ marginBottom: '12px' }}>
              Successfully imported {success.messageCount.toLocaleString()} messages! Honcho is now processing your history with ChatGPT to form a better representation of you.
            </Message>
          )}

          {/* Action Buttons */}
          <div style={{ 
            display: 'flex', 
            gap: '8px', 
            justifyContent: isMobile ? 'stretch' : 'flex-end',
            flexDirection: isMobile ? 'column' : 'row',
          }}>
            {selectedFile && !isProcessing && !isUploading && !success && !previewInfo && (
              <Button
                variant="secondary"
                size={isMobile ? 'md' : 'sm'}
                onClick={() => {
                  setSelectedFile(null)
                  setError(null)
                  setProgress('')
                  setPreviewInfo(null)
                  setTrimNotice(null)
                  if (fileInputRef.current) {
                    fileInputRef.current.value = ''
                  }
                }}
                style={isMobile ? { width: '100%' } : undefined}
              >
                Clear
              </Button>
            )}
            <Button
              variant="primary"
              size={isMobile ? 'md' : 'sm'}
              onClick={() => onClose()}
              disabled={isProcessing || isUploading}
              style={isMobile ? { width: '100%' } : undefined}
            >
              {success ? 'Done' : 'Close'}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

